import { zonedTodayInput } from './report-timezone';

/**
 * The date range the Card reconciliation panels (SuperNova and Others) start on
 * before the user picks anything.
 *
 *   end   = today − 4 days
 *   start = the first day of the month `end` falls in
 *
 * The 4-day offset exists because card data is not final until settlement has
 * caught up, so the newest days would show an incomplete picture.
 *
 * The single rule above covers both cases in the requirement:
 *
 *  - `end` lands in the current month (the usual case) — start is the 1st of
 *    the current month. Today 17 Aug → 01 Aug – 13 Aug.
 *  - `end` falls back into the previous month (the first four days of a month) —
 *    start follows it back, so the user sees the previous month rather than a
 *    stub of the new one. Today 03 Sep → 01 Aug – 30 Aug; today 04 Sep →
 *    01 Aug – 31 Aug.
 *
 * Note the previous-month case still ends at today − 4, *not* at the last day of
 * that month — on 03 Sep the 31st is not yet settled, so including it would
 * contradict the whole point of the offset. This matches the worked examples in
 * the requirement (Example 2 ends 30 Aug, not 31 Aug).
 *
 * Returns tz-neutral `YYYY-MM-DD` strings, the same shape the panels already
 * exchange with their date pickers and send to the API.
 *
 * @param tz reporting time zone — "today" must be the merchant's today, not the
 *           browser's, or a store west of the line gets yesterday's default.
 */
export function defaultCardReconciliationRange(tz: string): { dateFrom: string; dateTo: string } {
  const [year, month, day] = zonedTodayInput(tz).split('-').map(Number);

  // Calendar arithmetic only — UTC keeps the subtraction free of any DST shift.
  // `tz` has already been applied above when resolving which day "today" is.
  const end = new Date(Date.UTC(year, month - 1, day));
  end.setUTCDate(end.getUTCDate() - 4);

  const p = (n: number) => String(n).padStart(2, '0');
  const endYmd = `${end.getUTCFullYear()}-${p(end.getUTCMonth() + 1)}-${p(end.getUTCDate())}`;
  const startYmd = `${end.getUTCFullYear()}-${p(end.getUTCMonth() + 1)}-01`;

  return { dateFrom: startYmd, dateTo: endYmd };
}
